import { useState, useEffect } from 'react';
import { CarListing, NewCarFormData } from '../../types/dealership';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { SuccessModal } from './SuccessModal';
import { apiService, CreateCarListingRequest } from '../../services/api';
import { toast } from 'sonner';
import { ImageManagement } from './ImageManagement';
import { CarImage } from '../../types/dealership';
import { calculatePricing, extractYearFromCarName, calculateVehicleAge } from '../../utils/pricingCalculator';

interface AddNewCarsSectionProps {
  editingListing?: CarListing | null;
  onSave?: (data: NewCarFormData) => void;
  onCancel?: () => void;
  dealerId?: number; // Add dealer ID for API calls
}

export function AddNewCarsSection({ editingListing, onSave, onCancel, dealerId = 1 }: AddNewCarsSectionProps) {
  const [formData, setFormData] = useState<NewCarFormData>({
    carName: '',
    carMake: '',
    carModel: '',
    carBrand: '',
    vehicleYear: '',
    mileage: '',
    fuelType: '',
    transmission: '',
    vin: '',
    exteriorColor: '',
    interiorColor: '',
    carDescription: '',
    msrp: '',
    downPayment: '',
    minCreditScore: '',
    termDuration: '',
    monthlyPayment: '',
    otherRequirements: '',
    carImages: [],
    uploadedImageUrls: []
  });

  const [locationData, setLocationData] = useState({
    country: '',
    stateProvince: '',
    city: ''
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isEditMode = !!editingListing;

  // Load dealership location from localStorage on component mount
  useEffect(() => {
    const savedLocation = localStorage.getItem('dealershipLocation');
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        setLocationData(location);
      } catch (error) {
        console.error('Error parsing dealership location:', error);
      }
    }
  }, []);

  // Pre-populate form when editing
  useEffect(() => {
    if (editingListing) {
      console.log('AddNewCarsSection: Editing listing with ID:', editingListing.id);
      // Extract year from carName if available, or use year field if it exists
      const year = editingListing.year || extractYearFromCarName(editingListing.carName)?.toString() || '';
      setFormData({
        carName: editingListing.carName,
        carMake: editingListing.carMake,
        carModel: editingListing.carModel,
        carBrand: editingListing.carBrand,
        vehicleYear: year,
        mileage: editingListing.mileage,
        fuelType: editingListing.fuelType,
        transmission: editingListing.transmission,
        vin: editingListing.vin || '',
        exteriorColor: editingListing.exteriorColor,
        interiorColor: editingListing.interiorColor,
        carDescription: editingListing.carDescription,
        msrp: editingListing.msrp,
        downPayment: editingListing.downPayment || '',
        minCreditScore: editingListing.minCreditScore,
        termDuration: editingListing.termDuration,
        monthlyPayment: editingListing.monthlyPayment,
        otherRequirements: editingListing.otherRequirements,
        carImages: editingListing.carImages
      });
    }
  }, [editingListing]);

  // Real-time pricing calculation
  useEffect(() => {
    const calculate = async () => {
      const msrp = parseFloat(formData.msrp.replace(/[^0-9.]/g, '')) || 0;
      const mileage = parseFloat(formData.mileage.replace(/[^0-9.]/g, '')) || 0;
      const downPayment = parseFloat(formData.downPayment.replace(/[^0-9.]/g, '')) || 0;
      const year = parseInt(formData.vehicleYear) || null;

      console.log('Pricing calculation check:', { msrp, mileage, downPayment, year, vehicleYear: formData.vehicleYear });

      // Only calculate if we have minimum required data
      if (msrp > 0 && mileage >= 0 && year && year >= 1900 && year <= new Date().getFullYear() + 1) {
        const age = calculateVehicleAge(year);
        console.log('Calculating pricing with:', { msrp, age, mileage, downPayment });
        
        const pricingResult = await calculatePricing({
          msrp,
          age,
          mileage,
          downPayment
        });

        console.log('Pricing result:', pricingResult);

        if (!pricingResult.error) {
        // Update calculated fields only if they're different to avoid unnecessary re-renders
        setFormData(prev => {
          const newMonthly = pricingResult.monthly.toString();
          const newTerm = pricingResult.term.toString();
          const newMinScore = pricingResult.minScore.toString();
          
          // Only update if values have changed
          if (prev.monthlyPayment !== newMonthly || 
              prev.termDuration !== newTerm || 
              prev.minCreditScore !== newMinScore) {
            return {
              ...prev,
              monthlyPayment: newMonthly,
              termDuration: newTerm,
              minCreditScore: newMinScore
            };
          }
          return prev;
        });
      } else {
        // Clear calculated fields on error only if they're not already empty
        setFormData(prev => {
          if (prev.monthlyPayment || prev.termDuration || prev.minCreditScore) {
            return {
              ...prev,
              monthlyPayment: '',
              termDuration: '',
              minCreditScore: ''
            };
          }
          return prev;
        });
        }
      } else if (!msrp || !year) {
        // Clear calculated fields if required data is missing (but keep if mileage is just 0)
        setFormData(prev => {
          if (prev.monthlyPayment || prev.termDuration || prev.minCreditScore) {
            return {
              ...prev,
              monthlyPayment: '',
              termDuration: '',
              minCreditScore: ''
            };
          }
          return prev;
        });
      }
    };

    calculate();
  }, [formData.msrp, formData.mileage, formData.vehicleYear, formData.downPayment]);

  // Debug ImageManagement rendering
  useEffect(() => {
    console.log('AddNewCarsSection: About to render ImageManagement with carListingId:', editingListing?.id ? Number(editingListing.id) : 0);
    console.log('AddNewCarsSection: formData.carImages:', formData.carImages);
  }, [editingListing, formData.carImages]);

  const handleInputChange = (field: keyof NewCarFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  const convertFormDataToApiRequest = (formData: NewCarFormData): CreateCarListingRequest => {
    // Extract image URLs from CarImage objects or use uploaded URLs
    const imageUrls = formData.carImages.length > 0 
      ? formData.carImages.map(img => {
          if ('imageUrl' in img) {
            return img.imageUrl; // CarImage object
          } else {
            return ''; // File object - will be handled during upload
          }
        }).filter(url => url !== '')
      : formData.uploadedImageUrls || [];

    const downPaymentNum = parseFloat((formData.downPayment || '').replace(/[^0-9.]/g, ''));
    const year = parseInt(formData.vehicleYear || '', 10);
    const price = parseFloat((formData.msrp || '').replace(/[^0-9.]/g, ''));
    return {
      dealer_id: dealerId,
      make: formData.carMake.trim(),
      model: formData.carModel.trim(),
      year: !isNaN(year) && year >= 1900 ? year : new Date().getFullYear(),
      price: !isNaN(price) && price > 0 ? price : 0,
      monthly_payment: parseFloat(formData.monthlyPayment) || undefined,
      term_months: parseInt(formData.termDuration) || undefined,
      min_credit_score: parseInt(formData.minCreditScore) || undefined,
      down_payment: !isNaN(downPaymentNum) && downPaymentNum > 0 ? downPaymentNum : undefined,
      vehicle_type: formData.carBrand,
      mileage: parseInt(formData.mileage) || undefined,
      fuel_type: formData.fuelType,
      transmission: formData.transmission,
      vin: formData.vin || undefined,
      color: formData.exteriorColor || undefined,
      description: formData.carDescription || undefined,
      location: locationData.country && locationData.stateProvince && locationData.city 
        ? `${locationData.city}, ${locationData.stateProvince}, ${locationData.country}`
        : undefined,
      images: imageUrls.length > 0 ? imageUrls : undefined,
      features: formData.otherRequirements ? [formData.otherRequirements] : undefined
    };
  };

  const handlePublish = async () => {
    // Validate required fields before submitting
    const yearNum = parseInt(formData.vehicleYear || '', 10);
    const priceNum = parseFloat((formData.msrp || '').replace(/[^0-9.]/g, ''));
    if (!formData.carMake?.trim()) {
      toast.error('Please enter the car make.');
      return;
    }
    if (!formData.carModel?.trim()) {
      toast.error('Please enter the car model.');
      return;
    }
    if (!formData.vehicleYear?.trim() || isNaN(yearNum) || yearNum < 1900 || yearNum > new Date().getFullYear() + 1) {
      toast.error('Please enter a valid vehicle year.');
      return;
    }
    if (!formData.msrp?.trim() || isNaN(priceNum) || priceNum <= 0) {
      toast.error('Please enter a valid price (MSRP).');
      return;
    }
    if (!dealerId || dealerId < 1) {
      toast.error('Dealer session invalid. Please log in again.');
      return;
    }

    setIsLoading(true);
    try {
      const apiRequest = convertFormDataToApiRequest(formData);
      let createdListingId: number | null = null;
      
      if (isEditMode && editingListing) {
        // Update existing listing
        await apiService.updateCarListing(parseInt(editingListing.id), apiRequest);
        toast.success('Car listing updated successfully!');
        if (onSave) {
          onSave(formData);
        }
        createdListingId = parseInt(editingListing.id);
      } else {
        // Create new listing
        const newListing = await apiService.createCarListing(apiRequest);
        toast.success('Car listing created successfully!');
        createdListingId = newListing.id;
        
        // Upload images if any
        if (formData.carImages.length > 0 && createdListingId) {
          try {
            const uploadedImageUrls = [];
            for (const image of formData.carImages) {
              // Only upload File objects, skip CarImage objects as they're already uploaded
              if (image instanceof File) {
                const uploadResult = await apiService.uploadCarImage(createdListingId, image);
                uploadedImageUrls.push(uploadResult.imageUrl);
              }
            }
            
            // Update the listing with the uploaded image URLs
            if (uploadedImageUrls.length > 0) {
              await apiService.updateCarListing(createdListingId, {
                images: uploadedImageUrls
              });
              toast.success('Images uploaded and linked successfully!');
            }
          } catch (uploadError) {
            console.error('Error uploading images:', uploadError);
            toast.error('Car listing created but failed to upload images. Please try uploading images again.');
          }
        }
        
        // Reset form after successful creation
        setFormData({
          carName: '',
          carMake: '',
          carModel: '',
          carBrand: '',
          vehicleYear: '',
          mileage: '',
          fuelType: '',
          transmission: '',
          vin: '',
          exteriorColor: '',
          interiorColor: '',
          carDescription: '',
          msrp: '',
          downPayment: '',
          minCreditScore: '',
          termDuration: '',
          monthlyPayment: '',
          otherRequirements: '',
          carImages: [],
          uploadedImageUrls: []
        });
      }
      
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving car listing:', error);
      toast.error(isEditMode ? 'Failed to update car listing. Please try again.' : 'Failed to create car listing. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSuccessAction = (action: string) => {
    console.log('Success action:', action);
    // Handle different actions based on the action type
    if (action === 'addAnother') {
      // Reset form for adding another car
      setFormData({
        carName: '',
        carMake: '',
        carModel: '',
        carBrand: '',
        vehicleYear: '',
        mileage: '',
        fuelType: '',
        transmission: '',
        exteriorColor: '',
        interiorColor: '',
        carDescription: '',
        msrp: '',
        downPayment: '',
        minCreditScore: '',
        termDuration: '',
        monthlyPayment: '',
        otherRequirements: '',
        carImages: [],
        vin: ''
      });
    }
  };

  return (
    <>
      <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto pb-8">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              {isEditMode ? `Edit Listing: ${editingListing?.model}` : 'Add New Car Listing'}
            </h1>
            <p className="text-gray-600 mt-2">
              {isEditMode ? 'Update your car listing information' : 'Create a new car listing for your inventory'}
            </p>
          </div>
          {isEditMode && (
            <Button
              onClick={handleCancel}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Car Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl text-[#f91bdc]">Car Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Car Name</label>
              <Input
                value={formData.carName}
                onChange={(e) => handleInputChange('carName', e.target.value)}
                placeholder="Enter car name"
                className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Vehicle Year *</label>
                <Input
                  type="number"
                  value={formData.vehicleYear}
                  onChange={(e) => handleInputChange('vehicleYear', e.target.value)}
                  placeholder="e.g., 2020"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
                <p className="text-xs text-gray-500">Required for pricing calculation</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Car Make</label>
                <Input
                  value={formData.carMake}
                  onChange={(e) => handleInputChange('carMake', e.target.value)}
                  placeholder="Enter car make"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Car Model</label>
                <Input
                  value={formData.carModel}
                  onChange={(e) => handleInputChange('carModel', e.target.value)}
                  placeholder="Enter car model"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Car Brand</label>
              <Input
                value={formData.carBrand}
                onChange={(e) => handleInputChange('carBrand', e.target.value)}
                placeholder="Enter car brand"
                className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Upload Car Pictures</label>
              
              <ImageManagement
                carListingId={editingListing?.id ? Number(editingListing.id) : 0}
                currentImages={formData.carImages as CarImage[]}
                onImagesChange={(images) => {
                  console.log('AddNewCarsSection: Images changed:', images);
                  setFormData(prev => ({
                    ...prev,
                    carImages: images,
                    uploadedImageUrls: images.map(img => img.imageUrl)
                  }));
                }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Mileage</label>
                <Input
                  value={formData.mileage}
                  onChange={(e) => handleInputChange('mileage', e.target.value)}
                  placeholder="Enter mileage"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Fuel Type</label>
                <Select value={formData.fuelType} onValueChange={(value) => handleInputChange('fuelType', value)}>
                  <SelectTrigger className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]">
                    <SelectValue placeholder="Select fuel type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gasoline">Gasoline</SelectItem>
                    <SelectItem value="diesel">Diesel</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="electric">Electric</SelectItem>
                    <SelectItem value="flex-fuel">Flex Fuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Transmission</label>
                <Select value={formData.transmission} onValueChange={(value) => handleInputChange('transmission', value)}>
                  <SelectTrigger className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]">
                    <SelectValue placeholder="Select transmission" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="automatic">Automatic</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="cvt">CVT</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">VIN Number</label>
                <Input
                  value={formData.vin}
                  onChange={(e) => handleInputChange('vin', e.target.value)}
                  placeholder="Enter VIN number"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Exterior Color</label>
                <Input
                  value={formData.exteriorColor}
                  onChange={(e) => handleInputChange('exteriorColor', e.target.value)}
                  placeholder="Enter exterior color"
                  className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Interior Color</label>
              <Input
                value={formData.interiorColor}
                onChange={(e) => handleInputChange('interiorColor', e.target.value)}
                placeholder="Enter interior color"
                className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Car Description</label>
              <Textarea
                value={formData.carDescription}
                onChange={(e) => handleInputChange('carDescription', e.target.value)}
                placeholder="Enter car description..."
                rows={4}
                className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc] resize-none"
              />
            </div>

            {/* Location Information (Auto-filled) */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Location</label>
              <div className="p-3 bg-gray-50 border-2 border-gray-200 rounded-lg">
                {locationData.country && locationData.stateProvince && locationData.city ? (
                  <p className="text-sm text-gray-700">
                    {locationData.city}, {locationData.stateProvince}, {locationData.country}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    Please complete your dealership profile to auto-fill location information
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Location is automatically filled from your dealership profile
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl text-[#f91bdc]">Pricing Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">MSRP *</label>
              <Input
                value={formData.msrp}
                onChange={(e) => handleInputChange('msrp', e.target.value)}
                placeholder="Enter MSRP"
                className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
              />
              <p className="text-xs text-gray-500">Required for pricing calculation</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Down Payment</label>
              <Input
                type="number"
                value={formData.downPayment}
                onChange={(e) => handleInputChange('downPayment', e.target.value)}
                placeholder="Enter down payment (default: 0)"
                className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc]"
              />
              <p className="text-xs text-gray-500">Optional - defaults to $0 if not specified</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Monthly Payment</label>
              <Input
                value={formData.monthlyPayment}
                readOnly
                placeholder="Auto-calculated"
                className="border-2 border-gray-200 bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500">Automatically calculated based on MSRP, age, mileage, and down payment</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Term Duration</label>
              <Input
                value={formData.termDuration ? `${formData.termDuration} Months` : ''}
                readOnly
                placeholder="Auto-calculated"
                className="border-2 border-gray-200 bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500">Automatically calculated based on vehicle age and mileage tiers</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Minimum Credit Score</label>
              <Input
                value={formData.minCreditScore}
                readOnly
                placeholder="Auto-calculated"
                className="border-2 border-gray-200 bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500">Automatically calculated based on source score and safety margin</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Other Requirements</label>
              <Textarea
                value={formData.otherRequirements}
                onChange={(e) => handleInputChange('otherRequirements', e.target.value)}
                placeholder="Enter any other requirements..."
                rows={4}
                className="border-2 border-[#f91bdc]/30 focus:border-[#f91bdc] resize-none"
              />
            </div>

            <Button 
              onClick={handlePublish}
              disabled={isLoading}
              className="w-full bg-[#f91bdc] hover:bg-[#e016c7] text-white py-3 rounded-lg font-semibold text-lg mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Publishing...' : (isEditMode ? 'Save Changes' : 'Publish Listing')}
            </Button>
          </CardContent>
        </Card>
      </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        type={isEditMode ? 'listingUpdated' : 'listingPublished'}
        actions={isEditMode ? undefined : [
          {
            label: 'View Listing',
            onClick: () => handleSuccessAction('viewListing'),
            variant: 'secondary'
          },
          {
            label: 'Add Another Car',
            onClick: () => handleSuccessAction('addAnother'),
            variant: 'primary'
          }
        ]}
      />
    </>
  );
}